import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { TournamentOperatorPlaybook } from "@/components/tournaments/operator-playbook";
import { ProspectusCard } from "@/components/tournaments/prospectus-card";
import { TournamentSanctionCard } from "@/components/tournaments/sanction-card";
import { TournamentSettingsDialog } from "@/components/tournaments/tournament-settings-dialog";
import { getFacility, type FacilityDetail } from "@/libs/facilityApi";
import {
  getTournamentOperatorPlaybook,
  getTournament,
  type TournamentOperatorPlaybook as TournamentOperatorPlaybookResponse,
  type Tournament,
  type TournamentStatus,
} from "@/libs/tournamentApi";
import { normalizeTournamentType } from "@/libs/tournamentTypes";
import { playerAppOrigin } from "@/libs/playerAppOrigin";
import { getTournamentSetup } from "@/libs/tournamentSetupApi";
import type { TournamentSetupView } from "@/libs/tournamentSetupApi";
import { EventsWorkspace } from "./events/events-workspace";
import { TimeRestSettings } from "./events/time-rest-settings";
import { OpenRegistrationDialog } from "./open-registration-dialog";
import { OverviewTab } from "./overview/overview-tab";
import { GlossaryProvider } from "./setup/glossary-drawer";
import { INFO_HIT_AREA } from "./setup/term-help";
import { DenseButton, Pill, SetupCard, SetupSurface, STAFF_FONT, tone } from "./setup/ui";
import { StatusActions } from "./status-actions";
import {
  MORE_ITEMS,
  OLD_TAB_MAP,
  resolveTournamentTab,
  SECTIONS,
  TAB_LABELS,
  TOURNAMENT_TABS,
} from "./tournament-tabs";
import type { TabKey } from "./tournament-tabs";

const tabLoading = (
  <Stack
    direction="row"
    role="status"
    spacing={1}
    sx={{ alignItems: "center", justifyContent: "center", minHeight: 160 }}
  >
    <CircularProgress size={20} />
    <Typography color="text.secondary" variant="body2">
      Loading…
    </Typography>
  </Stack>
);

const BracketTab = lazy(() =>
  import("@/components/tournaments/bracket-tab").then((module) => ({
    default: module.BracketTab,
  })),
);
const TournamentScheduleTab = lazy(() =>
  import("@/components/tournaments/tournament-schedule-tab").then((module) => ({
    default: module.TournamentScheduleTab,
  })),
);
const CertificatesTab = lazy(() =>
  import("@/components/tournaments/certificates-tab").then((module) => ({
    default: module.CertificatesTab,
  })),
);
const VenueDisplayTab = lazy(() =>
  import("@/components/tournaments/venue-display-tab").then((module) => ({
    default: module.VenueDisplayTab,
  })),
);
const DrawsTab = lazy(() =>
  import("@/components/tournaments/draws-tab").then((module) => ({
    default: module.DrawsTab,
  })),
);
const MerchandiseTab = lazy(() =>
  import("@/components/tournaments/merchandise-tab").then((module) => ({
    default: module.MerchandiseTab,
  })),
);
const SeriesTab = lazy(() =>
  import("@/components/tournaments/series-tab").then((module) => ({
    default: module.SeriesTab,
  })),
);
const TournamentRegistrations = lazy(() =>
  import("@/components/tournaments/tournament-registrations").then(
    (module) => ({
      default: module.TournamentRegistrations,
    }),
  ),
);
const TeamsTab = lazy(() =>
  import("@/components/tournaments/teams-tab").then((module) => ({
    default: module.TeamsTab,
  })),
);
const WinnersTab = lazy(() =>
  import("@/components/tournaments/winners-tab").then((module) => ({
    default: module.WinnersTab,
  })),
);
const ResultsReportsTab = lazy(() =>
  import("@/components/tournaments/results-reports-tab").then((module) => ({
    default: module.ResultsReportsTab,
  })),
);
const CloseoutTab = lazy(() =>
  import("@/components/tournaments/closeout-tab").then((module) => ({
    default: module.CloseoutTab,
  })),
);

// Old tab names from the playbook and the sanction card; the legacy map sends
// each one to its place in the seven tabs (staff → Overview › Officials).
type PlaybookTabDestination =
  | "overview"
  | "events"
  | "entries"
  | "draws"
  | "schedule"
  | "closeout"
  | "reports"
  | "staff";

const STATUS_COLOR: Record<TournamentStatus, ChipProps["color"]> = {
  draft: "default",
  published: "primary",
  open: "primary",
  registration_open: "success",
  registration_closed: "warning",
  in_progress: "secondary",
  completed: "success",
  cancelled: "error",
  archived: "default",
};

const fmtDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "-";

const fmtLabel = (value: string | null | undefined) =>
  value ? value.replace(/_/g, " ") : "-";
const fmtScoringType = (value: string | null | undefined) => {
  if (value === "rally_point") return "Rally scoring";
  if (value === "traditional") return "Traditional scoring";
  return "Custom scoring";
};
const fmtBoolean = (value: boolean | null | undefined) =>
  value ? "Required" : "Not required";
const fmtMoney = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? "-" : String(value);
const fmtRefundValue = (value: unknown) =>
  typeof value === "string" || typeof value === "number" ? value : "-";
const fmtRefundTier = (tier: Record<string, unknown>) => {
  if (
    typeof tier.days_before === "number" &&
    typeof tier.percent === "number"
  ) {
    return `${tier.days_before}+ days before start: ${tier.percent}% refund`;
  }
  if (
    typeof tier.retained_fee === "string" ||
    typeof tier.retained_fee === "number"
  ) {
    return `Retained processing fee: $${tier.retained_fee}`;
  }
  const entered = fmtRefundValue(tier.events_entered);
  const withdrawn = fmtRefundValue(tier.events_withdrawn);
  const amount = fmtMoney(fmtRefundValue(tier.refund_amount));
  return (
    "Entered " +
    String(entered) +
    ", withdrawn " +
    String(withdrawn) +
    ": " +
    (amount === "-" ? amount : "$" + amount)
  );
};
const formatFacilityAddress = (facility: FacilityDetail | null) => {
  const address = facility?.address;
  if (!address) return null;

  const locality = [address.city, address.state, address.postal_code]
    .filter(Boolean)
    .join(", ");
  return [address.line1, locality, address.country].filter(Boolean);
};

type TimelineMilestone = {
  label: string;
  value: string;
};

const utcDay = (date: Date) =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        display: "grid",
        gap: 0.5,
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "minmax(140px, 0.4fr) minmax(0, 1fr)",
        },
        py: 1.25,
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {typeof children === "string" || typeof children === "number" ? (
          <Typography variant="body2">{children}</Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}

function InformationSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box component="section" sx={{ minWidth: 0 }}>
      <Typography component="h3" sx={{ fontWeight: 700 }} variant="subtitle2">
        {title}
      </Typography>
      <Box sx={{ mt: 0.75 }}>{children}</Box>
    </Box>
  );
}

function TournamentTimeline({
  milestones,
}: {
  milestones: TimelineMilestone[];
}) {
  const today = utcDay(new Date());

  if (!milestones.length) {
    return <Typography variant="body2">-</Typography>;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: { xs: 1, md: 0 },
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: Array(milestones.length).fill("minmax(0, 1fr)").join(" "),
        },
      }}
    >
      {milestones.map((milestone, index) => {
        const isPast = utcDay(new Date(milestone.value)) < today;

        return (
          <Box
            key={milestone.label + "-" + milestone.value}
            sx={{
              borderColor: isPast ? "text.disabled" : "primary.main",
              borderLeft: { xs: 3, md: 0 },
              borderTop: { xs: 0, md: 3 },
              minWidth: 0,
              pb: { xs: 1, md: 0 },
              pl: { xs: 1.5, md: 0 },
              pt: { xs: 0, md: 1.25 },
              pr: { md: index < milestones.length - 1 ? 1.5 : 0 },
            }}
          >
            <Typography
              color={isPast ? "text.secondary" : "primary.main"}
              sx={{ fontWeight: 700 }}
              variant="caption"
            >
              {milestone.label}
            </Typography>
            <Typography variant="body2">{fmtDate(milestone.value)}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function Overview({
  tournament,
  onUpdated,
  onNavigateToControlCenter,
  onNavigateToTab,
  facility,
  facilityLoading,
}: {
  tournament: Tournament;
  onUpdated: () => void | Promise<void>;
  onNavigateToControlCenter: () => void;
  onNavigateToTab: (destination: PlaybookTabDestination) => void;
  facility: FacilityDetail | null;
  facilityLoading: boolean;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [playbook, setPlaybook] =
    useState<TournamentOperatorPlaybookResponse | null>(null);
  const [playbookLoading, setPlaybookLoading] = useState(true);
  const lifecycleStatus = (tournament.effective_status ??
    tournament.status) as TournamentStatus;
  const displayStatus: TournamentStatus =
    tournament.is_published && lifecycleStatus === "draft"
      ? "open"
      : lifecycleStatus;
  const registrationOpen = tournament.registration_open_at;
  const registrationClose = tournament.registration_close_at;
  const scoring = tournament.scoring_system;
  const logistics = tournament.tournament_logistics;
  const pricingTiers = tournament.pricing_tiers ?? [];
  const refundTiers = tournament.refund_tiers ?? [];
  const facilityAddress = formatFacilityAddress(facility);
  const timelineMilestones: TimelineMilestone[] = [
    { label: "Registration opens", value: registrationOpen },
    { label: "Registration closes", value: registrationClose },
    { label: "Refund deadline", value: tournament.refund_deadline_at },
    { label: "Tournament start", value: tournament.start_date },
    { label: "Tournament end", value: tournament.end_date },
  ].filter((milestone): milestone is TimelineMilestone =>
    Boolean(milestone.value),
  );

  useEffect(() => {
    let active = true;
    setPlaybook(null);
    setPlaybookLoading(true);
    void getTournamentOperatorPlaybook(tournament.tournament_id)
      .then((nextPlaybook) => {
        if (active) setPlaybook(nextPlaybook);
      })
      .catch(() => {
        if (active) setPlaybook(null);
      })
      .finally(() => {
        if (active) setPlaybookLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tournament]);

  return (
    <Stack spacing={2}>
      <Paper component="section" variant="outlined">
        <Stack spacing={2.5} sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography component="h2" variant="h6">
                All settings
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Every stored setting, the lifecycle state and the organizer playbook.
              </Typography>
            </Box>
            <Button
              onClick={() => setSettingsOpen(true)}
              startIcon={<EditOutlinedIcon />}
              variant="outlined"
            >
              Edit settings
            </Button>
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Stack spacing={0.75}>
                <Typography color="text.secondary" variant="body2">
                  Current status
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Chip
                    color={STATUS_COLOR[displayStatus] ?? "default"}
                    label={fmtLabel(displayStatus)}
                    size="small"
                    sx={{ textTransform: "capitalize" }}
                    variant={displayStatus === "draft" ? "outlined" : "filled"}
                  />
                  <Typography color="text.secondary" variant="body2">
                    {tournament.is_published ? "Published" : "Not published"}
                  </Typography>
                </Stack>
              </Stack>
              <StatusActions hidePublish onUpdated={onUpdated} tournament={tournament} />
            </Stack>

            <FieldRow label="Timeline">
              <TournamentTimeline milestones={timelineMilestones} />
            </FieldRow>
          </Stack>

          <Divider />

          {playbookLoading ? (
            <Typography color="text.secondary" role="status" variant="body2">
              Loading operator guidance…
            </Typography>
          ) : (
            <TournamentOperatorPlaybook
              onNavigateToControlCenter={onNavigateToControlCenter}
              onNavigateToTab={onNavigateToTab}
              playbook={playbook}
              tournamentId={tournament.tournament_id}
            />
          )}

          <Divider />

          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <InformationSection title="Format and venue">
              <FieldRow label="Type">
                <Typography
                  sx={{ textTransform: "capitalize" }}
                  variant="body2"
                >
                  {fmtLabel(tournament.tournament_type)}
                </Typography>
              </FieldRow>
              <FieldRow label="Venue">
                {tournament.venue_name || tournament.venue_address ? (
                  <Stack spacing={0.25}>
                    {tournament.venue_name ? (
                      <Typography variant="body2">
                        {tournament.venue_name}
                      </Typography>
                    ) : null}
                    {tournament.venue_address ? (
                      <Typography color="text.secondary" variant="body2">
                        {tournament.venue_address}
                      </Typography>
                    ) : null}
                  </Stack>
                ) : facilityLoading ? (
                  <Typography color="text.secondary" variant="body2">
                    Loading facility…
                  </Typography>
                ) : facility ? (
                  <Stack spacing={0.25}>
                    <Typography variant="body2">{facility.name}</Typography>
                    {facilityAddress?.length ? (
                      <Typography color="text.secondary" variant="body2">
                        {facilityAddress.join(" · ")}
                      </Typography>
                    ) : null}
                  </Stack>
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </FieldRow>
              <FieldRow label="Description">
                {tournament.description ?? "-"}
              </FieldRow>
            </InformationSection>

            <InformationSection title="Entry policies">
              <FieldRow label="Pricing model">
                <Typography
                  sx={{ textTransform: "capitalize" }}
                  variant="body2"
                >
                  {fmtLabel(tournament.pricing_model)}
                </Typography>
              </FieldRow>
              {tournament.pricing_model === "tiered_volume" ? (
                <FieldRow label="Pricing tiers">
                  {pricingTiers.length ? (
                    <Stack spacing={0.25}>
                      {pricingTiers.map((tier, index) => (
                        <Typography key={String(index)} variant="body2">
                          {tier.event_count} event
                          {tier.event_count === 1 ? "" : "s"}:{" "}
                          {fmtMoney(tier.fee)}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </FieldRow>
              ) : null}
              <FieldRow label="Refund policy">
                {tournament.refund_policy_summary ?? "-"}
              </FieldRow>
              <FieldRow label="Refund schedule">
                {refundTiers.length ? (
                  <Stack spacing={0.25}>
                    {refundTiers.map((tier, index) => (
                      <Typography key={String(index)} variant="body2">
                        {fmtRefundTier(tier)}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </FieldRow>
            </InformationSection>

            <InformationSection title="Rules">
              <FieldRow label="Governing organization">
                {tournament.required_federation ?? "-"}
              </FieldRow>
              <FieldRow label="Coach pass">
                {fmtBoolean(tournament.coach_pass_required)}
              </FieldRow>
              <FieldRow label="Scoring">
                {scoring
                  ? fmtScoringType(scoring.type) +
                    ", " +
                    String(scoring.games_per_match) +
                    " games, " +
                    String(scoring.points_per_game) +
                    " points, setting " +
                    String(scoring.setting_point)
                  : "-"}
              </FieldRow>
              <FieldRow label="Match duration">
                {tournament.match_duration_minutes
                  ? String(tournament.match_duration_minutes) + " minutes"
                  : "-"}
              </FieldRow>
            </InformationSection>

            <InformationSection title="Contacts and travel">
              <FieldRow label="Organizer email">
                {tournament.organizer_email ?? "-"}
              </FieldRow>
              <FieldRow label="Organizer phone">
                {tournament.organizer_phone ?? "-"}
              </FieldRow>
              <FieldRow label="Organizer website">
                {tournament.organizer_website ?? "-"}
              </FieldRow>
              <FieldRow label="Hotel">{logistics?.hotel?.name ?? "-"}</FieldRow>
              <FieldRow label="Airports">
                {logistics?.airports?.length
                  ? logistics.airports
                      .map(
                        (airport) =>
                          airport.code +
                          (airport.name ? " (" + airport.name + ")" : ""),
                      )
                      .join(", ")
                  : "-"}
              </FieldRow>
              <FieldRow label="Parking">
                {logistics?.parking?.notes || logistics?.parking?.fee
                  ? [
                      logistics.parking.notes,
                      logistics.parking.fee
                        ? "Fee " + fmtMoney(logistics.parking.fee)
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "-"}
              </FieldRow>
            </InformationSection>
          </Box>
        </Stack>
      </Paper>

      <TournamentSanctionCard
        tournament={tournament}
        onOpenTab={onNavigateToTab}
      />
      <TournamentSettingsDialog
        onClose={() => setSettingsOpen(false)}
        onSaved={onUpdated}
        open={settingsOpen}
        tournament={tournament}
      />
      <ProspectusCard tournament={tournament} onUpdated={onUpdated} />
    </Stack>
  );
}

export function TournamentDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [setup, setSetup] = useState<TournamentSetupView | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [facility, setFacility] = useState<FacilityDetail | null>(null);
  const [facilityLoading, setFacilityLoading] = useState(true);
  const [facilityResolvedForId, setFacilityResolvedForId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tournamentResolvedForId, setTournamentResolvedForId] = useState<string | null>(null);
  const [openRegistration, setOpenRegistration] = useState(false);
  const [timeSettingsOpen, setTimeSettingsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const currentTournamentIdRef = useRef(id);
  currentTournamentIdRef.current = id;
  const resolved = resolveTournamentTab(searchParams.get("tab"), searchParams.get("section"));
  // An old ?tab= key (bookmarks, journeys) is rewritten in place to its new home.
  const legacyTab = searchParams.get("tab");
  useEffect(() => {
    if (!legacyTab || legacyTab === resolved.tab) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("tab", resolved.tab);
        if (resolved.section) next.set("section", resolved.section);
        else next.delete("section");
        return next;
      },
      { replace: true },
    );
  }, [legacyTab, resolved.tab, resolved.section, setSearchParams]);
  const activeTab = resolved.tab;
  const sections = SECTIONS[activeTab];
  const activeSection = sections ? (sections.find((s) => s.key === resolved.section)?.key ?? sections[0].key) : undefined;

  const navigateTournamentTab = useCallback(
    (tab: string, section?: string) => {
      const target = resolveTournamentTab(tab, section ?? null);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set("tab", target.tab);
        if (target.section) next.set("section", target.section);
        else next.delete("section");
        return next;
      });
    },
    [setSearchParams],
  );
  const navigateToControlCenter = useCallback(() => {
    navigate(`/tournaments/${id}/control-center`);
  }, [id, navigate]);
  // A playbook step moves focus to the tab it opens, as the old shell did.
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);
  const navigateFromPlaybook = useCallback(
    (tab: PlaybookTabDestination) => {
      const target = resolveTournamentTab(tab, null);
      setPendingFocus(target.section ? `tournament-section-${target.section}` : `tournament-tab-${target.tab}`);
      navigateTournamentTab(tab);
    },
    [navigateTournamentTab],
  );
  useEffect(() => {
    if (!pendingFocus) return;
    const element = document.getElementById(pendingFocus);
    if (element instanceof HTMLElement) {
      element.focus();
      setPendingFocus(null);
    }
  });

  const refreshTournament = useCallback(async () => {
    const data = await getTournament(id);
    if (currentTournamentIdRef.current === id) setTournament(data);
  }, [id]);
  const refreshSetup = useCallback(async () => {
    try {
      const view = await getTournamentSetup(id);
      if (currentTournamentIdRef.current === id) {
        setSetup(view);
        setSetupError(null);
      }
    } catch (cause) {
      if (currentTournamentIdRef.current === id) {
        setSetupError(cause instanceof Error ? cause.message : "The setup couldn’t load.");
      }
    }
  }, [id]);
  const onSetupChange = useCallback(
    (next: TournamentSetupView) => {
      setSetup(next);
      void refreshTournament();
    },
    [refreshTournament],
  );

  const tournamentFacilityId = tournament?.facility_id ?? null;
  useEffect(() => {
    if (!tournamentFacilityId) {
      setFacility(null);
      setFacilityLoading(true);
      setFacilityResolvedForId(null);
      return;
    }
    let cancelled = false;
    setFacilityLoading(true);
    setFacility(null);
    setFacilityResolvedForId(null);
    void (async () => {
      try {
        const data = await getFacility(tournamentFacilityId);
        if (!cancelled) setFacility(data);
      } catch {
        if (!cancelled) setFacility(null);
      } finally {
        if (!cancelled) {
          setFacilityResolvedForId(tournamentFacilityId);
          setFacilityLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentFacilityId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTournament(null);
    setSetup(null);
    setTournamentResolvedForId(null);
    void (async () => {
      try {
        const data = await getTournament(id);
        if (!cancelled && currentTournamentIdRef.current === id) setTournament(data);
      } catch (cause) {
        if (!cancelled && currentTournamentIdRef.current === id) {
          setError(cause instanceof Error ? cause.message : "Failed to load tournament");
        }
      } finally {
        if (!cancelled && currentTournamentIdRef.current === id) {
          setTournamentResolvedForId(id);
          setLoading(false);
        }
      }
    })();
    void refreshSetup();
    return () => {
      cancelled = true;
    };
  }, [id, refreshSetup]);

  if (loading || tournamentResolvedForId !== id) {
    return (
      <Stack direction="row" role="status" spacing={1} sx={{ alignItems: "center", justifyContent: "center", minHeight: 240, px: { xs: 2, lg: 3 } }}>
        <CircularProgress size={22} />
        <Typography color="text.secondary" variant="body2">
          Loading tournament…
        </Typography>
      </Stack>
    );
  }

  if (error || !tournament) {
    return (
      <Box sx={{ px: { xs: 2, lg: 3 } }}>
        <Alert severity="error">{error ?? "Tournament not found"}</Alert>
      </Box>
    );
  }

  const facilityMatchesTournament = facilityResolvedForId === tournament.facility_id;
  const currentFacility = facilityMatchesTournament ? facility : null;
  const currentFacilityLoading = facilityLoading || !facilityMatchesTournament;
  const tournamentFacilityTimeZone = tournament.facility_timezone?.trim() ?? "";
  const facilityTimeZone = tournamentFacilityTimeZone || currentFacility?.timezone?.trim() || "";
  const facilityTimezoneLoading = !tournamentFacilityTimeZone && currentFacilityLoading;
  const canOpen = Boolean(setup?.allowed_actions.includes("open_registration"));
  const missing = setup?.missing_required ?? [];
  const scheduleNeedsTimezone = (content: ReactNode) => {
    if (facilityTimezoneLoading) return tabLoading;
    return facilityTimeZone ? content : (
      <Alert severity="error">Facility timezone unavailable. Match scheduling is unavailable.</Alert>
    );
  };

  const renderActiveContent = () => {
    switch (activeTab) {
      case "overview":
        if (!setup) return setupError ? <Alert severity="error">{setupError}</Alert> : tabLoading;
        return (
          <OverviewTab
            facilityId={tournament.facility_id}
            initialSection={resolved.section}
            onEditTime={() => setTimeSettingsOpen(true)}
            onOpenEvents={() => navigateTournamentTab("events")}
            onOpenSettings={() => setSettingsOpen(true)}
            onViewChange={onSetupChange}
            view={setup}
          />
        );
      case "events":
        if (!setup) return setupError ? <Alert severity="error">{setupError}</Alert> : tabLoading;
        return (
          <EventsWorkspace
            onEditTime={() => setTimeSettingsOpen(true)}
            onOpenOverview={() => navigateTournamentTab("overview")}
            onViewChange={onSetupChange}
            view={setup}
          />
        );
      case "entries":
        return activeSection === "teams" ? (
          <TeamsTab tournament={tournament} tournamentId={id} />
        ) : (
          <TournamentRegistrations />
        );
      case "draws":
        if (activeSection === "bracket") {
          return scheduleNeedsTimezone(
            <BracketTab
              facilityTimeZone={facilityTimeZone}
              tournamentId={id}
              tournamentType={normalizeTournamentType(tournament.tournament_type)}
            />,
          );
        }
        return <DrawsTab tournamentId={id} tournamentType={normalizeTournamentType(tournament.tournament_type)} />;
      case "schedule":
        return scheduleNeedsTimezone(<TournamentScheduleTab tournamentId={id} venueTimeZone={facilityTimeZone} />);
      case "run":
        return activeSection === "displays" ? (
          <VenueDisplayTab tournamentId={id} />
        ) : (
          <RunTournamentCard onOpen={navigateToControlCenter} />
        );
      case "results":
        if (activeSection === "winners") return <WinnersTab tournamentId={id} />;
        if (activeSection === "reports") return <ResultsReportsTab tournamentId={id} />;
        if (activeSection === "certificates") return <CertificatesTab tournamentId={id} />;
        return <CloseoutTab onNavigateToTab={(tab) => navigateTournamentTab(tab)} tournamentId={id} />;
      case "merchandise":
        return <MerchandiseTab tournamentId={id} />;
      case "series":
        return <SeriesTab onUpdated={refreshTournament} tournament={tournament} />;
      case "settings":
        return (
          <Overview
            facility={currentFacility}
            facilityLoading={currentFacilityLoading}
            onNavigateToControlCenter={navigateToControlCenter}
            onNavigateToTab={navigateFromPlaybook}
            onUpdated={refreshTournament}
            tournament={tournament}
          />
        );
    }
  };

  const tabValue = (TOURNAMENT_TABS as readonly string[]).includes(activeTab) ? activeTab : false;
  const eventCount = setup?.events.length ?? null;

  return (
    <GlossaryProvider>
    <SetupSurface sx={{ mt: { xs: -2, sm: -2.5 } }}>
      <Box sx={{ minHeight: 48, display: "flex", alignItems: "center", gap: 1, px: "20px", py: { xs: 1, md: 0 }, bgcolor: tone.surface, flexWrap: "wrap" }}>
        <Box component={RouterLink} sx={{ color: tone.subtle, fontWeight: 500, textDecoration: "none" }} to="/tournaments">
          Competitions
        </Box>
        <Box component="span" sx={{ color: tone.faint }}>
          ›
        </Box>
        <Box component="h1" sx={{ m: 0, fontSize: 14, fontWeight: 700 }}>
          {tournament.name}
        </Box>
        <Pill>{setup?.status_label ?? fmtLabel(tournament.effective_status ?? tournament.status)}</Pill>
        <Box component="span" sx={{ color: tone.subtle, fontSize: 12.5, ml: "6px", display: { xs: "none", md: "inline" } }}>
          {setup?.header_line ?? `${fmtDate(tournament.start_date)} – ${fmtDate(tournament.end_date)}`}
        </Box>
        {/* The two actions stay together, right-aligned, when the header wraps. */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto", flex: "none" }}>
          <DenseButton onClick={() => window.open(`${playerAppOrigin()}/tournaments/${id}`, "_blank", "noopener")}>
            Preview public page
          </DenseButton>
          {setup && (canOpen || setup.status === "draft") ? (
            <Tooltip title={canOpen ? "" : `Finish these first: ${missing.join(", ")}.`}>
              <span>
                <DenseButton disabled={!canOpen} onClick={() => setOpenRegistration(true)} variant="primary">
                  Open registration
                </DenseButton>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      <Box component="nav" sx={{ minHeight: 40, display: "flex", alignItems: "center", px: "12px", bgcolor: tone.surface, borderBottom: `1px solid ${tone.line}` }}>
        <Tabs
          allowScrollButtonsMobile
          aria-label="Tournament"
          onChange={(_, value: TabKey) => navigateTournamentTab(value)}
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { minHeight: 40, minWidth: 0, px: "12px", textTransform: "none", fontFamily: STAFF_FONT, fontWeight: 500, fontSize: 13, color: tone.muted },
            "& .Mui-selected": { color: `${tone.ink} !important`, fontWeight: 600 },
            "& .MuiTabs-indicator": { bgcolor: tone.brand, height: 2 },
          }}
          value={tabValue}
          variant="scrollable"
        >
          {TOURNAMENT_TABS.map((tab) => (
            <Tab
              aria-controls="tournament-active-panel"
              id={`tournament-tab-${tab}`}
              key={tab}
              label={
                tab === "events" && eventCount !== null ? (
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    Events
                    <Box component="span" sx={{ fontSize: 10.5, px: "5px", height: 16, borderRadius: "8px", bgcolor: tone.chip, color: tone.muted, display: "inline-flex", alignItems: "center", fontWeight: 600 }}>
                      {eventCount}
                    </Box>
                  </Box>
                ) : (
                  TAB_LABELS[tab]
                )
              }
              value={tab}
            />
          ))}
        </Tabs>
        <DenseButton
          aria-expanded={Boolean(moreAnchor)}
          aria-haspopup="menu"
          onClick={(event) => setMoreAnchor(event.currentTarget)}
          sx={{ border: 0, bgcolor: "transparent", color: (MORE_ITEMS as readonly string[]).includes(activeTab) ? tone.ink : tone.muted, fontWeight: 500 }}
        >
          More ▾
        </DenseButton>
        <Menu anchorEl={moreAnchor} onClose={() => setMoreAnchor(null)} open={Boolean(moreAnchor)}>
          {MORE_ITEMS.map((item) => (
            <MenuItem
              key={item}
              onClick={() => {
                setMoreAnchor(null);
                navigateTournamentTab(item);
              }}
              selected={activeTab === item}
            >
              {TAB_LABELS[item]}
            </MenuItem>
          ))}
        </Menu>
        <Tooltip
          title={
            <Box component="span" sx={{ display: "block", fontFamily: STAFF_FONT }}>
              {OLD_TAB_MAP.map((row) => (
                <Box component="span" key={row.old} sx={{ display: "block" }}>
                  <b>{row.old}</b> → {row.now}
                </Box>
              ))}
            </Box>
          }
        >
          <Box
            aria-label="Where did the old tabs go?"
            component="button"
            sx={{ ...INFO_HIT_AREA, width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${tone.faint}`, color: tone.subtle, font: "inherit", fontWeight: 700, fontSize: 10, bgcolor: "transparent", p: 0, ml: "2px", mr: "14px", flex: "none", cursor: "pointer" }}
            type="button"
          >
            i
          </Box>
        </Tooltip>
      </Box>
      {sections ? (
        <Box sx={{ px: "20px", bgcolor: tone.surface, borderBottom: `1px solid ${tone.divider}` }}>
          <Tabs
            aria-label={`${TAB_LABELS[activeTab]} sections`}
            onChange={(_, value: string) => navigateTournamentTab(activeTab, value)}
            sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, minWidth: 0, px: "10px", textTransform: "none", fontFamily: STAFF_FONT, fontSize: 12.5 } }}
            value={activeSection}
          >
            {sections.map((section) => (
              <Tab id={`tournament-section-${section.key}`} key={section.key} label={section.label} value={section.key} />
            ))}
          </Tabs>
        </Box>
      ) : null}
      <Box aria-labelledby={`tournament-tab-${activeTab}`} id="tournament-active-panel" role="tabpanel" sx={{ minWidth: 0, p: "16px 20px" }}>
        <Suspense fallback={tabLoading}>{renderActiveContent()}</Suspense>
      </Box>
      {setup ? (
        <OpenRegistrationDialog
          onChangeCourts={() => {
            setOpenRegistration(false);
            navigateTournamentTab("overview", "basics");
          }}
          onClose={() => setOpenRegistration(false)}
          onConflict={() => void refreshSetup()}
          onOpened={(next) => {
            setOpenRegistration(false);
            onSetupChange(next);
          }}
          open={openRegistration}
          view={setup}
        />
      ) : null}
      {setup && timeSettingsOpen ? (
        <TimeRestSettings onClose={() => setTimeSettingsOpen(false)} onSaved={onSetupChange} open={timeSettingsOpen} view={setup} />
      ) : null}
      <TournamentSettingsDialog onClose={() => setSettingsOpen(false)} onSaved={refreshTournament} open={settingsOpen} tournament={tournament} />
    </SetupSurface>
    </GlossaryProvider>
  );
}

function RunTournamentCard({ onOpen }: { onOpen: () => void }) {
  return (
    <SetupCard title="Run tournament">
      <Box sx={{ p: "12px 14px", display: "flex", alignItems: "center", gap: 2, fontSize: 13 }}>
        <span>Call matches, assign courts and follow scores in the control center.</span>
        <Box sx={{ flexGrow: 1 }} />
        <DenseButton onClick={onOpen} variant="primary">
          Open the control center
        </DenseButton>
      </Box>
    </SetupCard>
  );
}
